'use client';
/* Built In Imports */
import { useEffect, useState } from 'react';

/* External Imports */
import { ArrowForwardIcon } from '@chakra-ui/icons';
import { Link } from '@chakra-ui/next-js';
import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Spinner,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';

import { render } from 'datocms-structured-text-to-html-string';
import { nanoid } from 'nanoid';
import Slider from 'react-slick';
/* Internal Imports */
/* Components */
import WorkshopsCard from '@app-ps/Components/Card/WorkshopsCard';
import { refineUrlDomain } from '@app-ps/Components/Utility/utils';
import StructuredTextParser from '@app-ps/Utility/StructuredTextParser';
import config from '@config';

/* Services */
import { fetchUpcomingListData } from '@Services/projSamskritiService';

/* Styles */
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

/**
 * Upcoming List
 * @param {object} sectionContent - Data
 * @param {string} lang - Language of Page
 * @returns {ReactElement} Upcoming List
 */
const UpcomingList = ({ section, sectionContent, lang }) => {
  const [loading, setLoading] = useState(false);
  const [extractedObjects, setExtractedObjects] = useState([]);
  const [filterId, setFilterId] = useState('');
  const sectionTitle = sectionContent.filter(
    item => item._modelApiKey === 'section_title'
  )[0];

  const upcomingWorkshopPerformanceSection = sectionContent.filter(
    item => item._modelApiKey === 'ps_upcoming_workshop_performance'
  )[0];

  const viewLink = sectionContent.filter(
    item => item._modelApiKey === 'simple_link'
  )[0];

  const isMdOrLarger = useBreakpointValue({ base: false, md: true });

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const prgData = await fetchUpcomingListData(
          upcomingWorkshopPerformanceSection?.defaultCategoryId
        );
        setExtractedObjects(prgData);
        setLoading(false);
      } catch (err) {
        console.error('err', err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {}, [extractedObjects]);

  const NextArrow = props => {
    const { className, style, onClick } = props;
    return (
      <Box
        pos="absolute"
        textAlign={'center'}
        top={{ base: '25%', md: '45%' }}
        zIndex="15"
        cursor="pointer"
        border="0"
        borderRadius="100%"
        w="48px"
        h="48px"
        className={className}
        bgPos="center"
        bgRepeat="no-repeat"
        color="transparent"
        bgImage={`url(${config.imgPath}/d/46272/1692094409-right-arrow.svg)`}
        right={{ base: '2%', md: '-5%' }}
        onClick={onClick}
        _hover={{
          bgImage: `url(${config.imgPath}/d/46272/1692094409-right-arrow.svg)`,
          bgRepeat: 'no-repeat',
          bgSize: 'auto',
          bgPos: 'center',
        }}
        boxShadow="0 0 15px 0 rgb(0 0 0 / 30%)"
      ></Box>
    );
  };

  const PrevArrow = props => {
    const { className, style, onClick } = props;
    return (
      <Box
        pos="absolute"
        top={{ base: '25%', md: '45%' }}
        zIndex="15"
        className={className}
        cursor="pointer"
        border="0"
        borderRadius="100%"
        w="48px"
        h="48px"
        bgPos="center"
        bgRepeat="no-repeat"
        _hover={{
          bgImage: `url(${config.imgPath}/d/46272/1692094406-left-arrow.svg)`, // Corrected syntax
          bgRepeat: 'no-repeat',
          bgSize: 'auto',
          bgPos: 'center',
        }}
        color="transparent"
        bgImage={`url(${config.imgPath}/d/46272/1692094406-left-arrow.svg)`}
        left={{ base: '0.625rem', md: '-5%' }}
        onClick={onClick}
        boxShadow="0 0 15px 0 rgb(0 0 0 / 30%)"
      />
    );
  };

  const extractedLength = extractedObjects?.filter(el => {
    if (filterId !== '') {
      return filterId.indexOf(el.category_id) > -1;
    } else {
      return el;
    }
  })?.length;

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    // slidesToShow: 4,
    slidesToScroll: 4,
    cssEase: 'linear',
    className: 'upcomingSlider',
    initialSlide: 0,
    variableWidth: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    arrows: extractedLength > 4,
    swipe: extractedLength > 4,
    responsive: [
      {
        breakpoint: 1320,
        settings: {
          arrows: extractedLength > 3,
          slidesToScroll: 4,
          swipe: extractedLength > 3,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          arrows: false,
          slidesToScroll: 2,
          swipe: extractedLength > 3,
        },
      },
      {
        breakpoint: 468,
        settings: {
          arrows: false,
          slidesToScroll: 1,
          swipe: true,
        },
      },
      {
        breakpoint: 380,
        settings: {
          arrows: false,
          slidesToScroll: 1,
          swipe: true,
        },
      },
    ],
  };

  return (
    <Box
      overflowX={'hidden'}
      backgroundImage={
        section?.styleConfig?.administrativeName === 'blue-bg'
          ? 'url(https://static.sadhguru.org/d/46272/1730980395-bluebg.png)'
          : ''
      }
      backgroundSize="100% 100%"
      pt={{ base: '3rem', md: '11.563rem', lg: '6.25rem', '2xl': '12rem' }}
      pb={{
        base:
          section?.styleConfig?.administrativeName === 'blue-bg'
            ? '10rem'
            : '4rem',
        md:
          section?.styleConfig?.administrativeName === 'blue-bg'
            ? '9rem'
            : '5.375rem',
        lg: '8.25rem',
        xl: '14rem',
        '2xl': '18rem',
      }}
    >
      <Box maxW={{ base: '100%', '2xl': 1480 }} mx={'auto'}>
        <Box
          mx={'auto'}
          w="100%"
          mb="2rem"
          px={{ base: '1rem', lg: '5rem', '2xl': '0' }}
          display="flex"
          alignItems={{ md: 'center', base: 'end' }}
          justifyContent={{
            base: 'space-between',
          }}
        >
          <Heading
            fontSize={{ base: '1.5rem', sm: '1.75rem', lg: '2.25rem' }}
            lineHeight={{ md: '2.7rem' }}
            maxW="80%"
            // as="h3"
          >
            <Box
              display="inline-block"
              fontWeight="400"
              color="#FEFAF6"
              fontFamily="var(--FedraSerifDisplay-Regular)"
              marginRight={{ base: '0.5rem', md: '.5rem' }}
            >
              {sectionTitle?.titleText}
            </Box>

            {sectionTitle?.titleSubtext && (
              <Text
                as="span"
                mr={{ base: '6px', lg: '5px' }}
                color={
                  upcomingWorkshopPerformanceSection?.theme === 'workshop'
                    ? '#EDA807'
                    : '#F18128' // Default to production color if theme is empty
                }
                fontFamily="var(--FedraSerifDisplay-Bold)"
              >
                {sectionTitle?.titleSubtext}
              </Text>
            )}
            {sectionTitle?.additionalTextLine && (
              <Box
                as="span"
                color="#FEFAF6"
                fontWeight="700"
                fontFamily={'var(--FedraSerifDisplay-Bold)'}
              >
                {sectionTitle?.additionalTextLine}
              </Box>
            )}
            {/* {sectionTitle?.additionalTextLine && (
              <Box
                as="span"
                ml="10px"
                color="#FEFAF6"
                fontWeight="700"
                fontFamily={'var(--FedraSerifDisplay-Bold)'}
              >
                {sectionTitle?.additionalTextLine}
              </Box>
            )} */}
          </Heading>
          {viewLink && (
            <Link
              fontFamily={'var(--FedraSansStd-medium)'}
              fontWeight="400"
              fontSize="1rem"
              lineHeight="1.6rem"
              color="#F1EDE9"
              // color={
              //   upcomingWorkshopPerformanceSection?.theme === 'workshop'
              //     ? '#EDA807'
              //     : '#F18128'
              // }
              _hover={{ textDecoration: 'none' }}
              href={refineUrlDomain(viewLink.linkUrl, lang)}
              prefetch
            >
              {isMdOrLarger && viewLink.buttonText}
              {/* {isDesktop && viewLink.buttonText} */}
              <Box
                as="span"
                marginLeft="0.5rem"
                color={
                  upcomingWorkshopPerformanceSection?.theme === 'workshop'
                    ? '#EDA807'
                    : '#F18128'
                }
              >
                <ArrowForwardIcon
                  // w={'2rem'}
                  // h={'2rem'}
                  boxSize="24px"
                  color="#F1EDE9"
                  display={{ base: 'none', md: 'inline-block' }}
                />
                <Center
                  // ml="5px"
                  boxSize="44px"
                  // top={{ base: '0%', sm: '18%' }}
                  borderRadius="full"
                  bg="#1A1D1F"
                  display={{ base: 'flex', md: 'none' }}
                  // position="absolute"
                  right="7"
                >
                  <ArrowForwardIcon
                    boxSize="24px"
                    // color={
                    //   upcomingWorkshopPerformanceSection?.theme === 'workshop'
                    //     ? '#EDA807'
                    //     : '#F18128'
                    // }
                    color="#F1EDE9"
                  />
                </Center>
              </Box>
            </Link>
          )}
        </Box>

        <Box marginLeft={{ lg: '14%', '2xl': '4%' }} mt={{ lg: '5%' }}>
          {upcomingWorkshopPerformanceSection?.programTypeList?.json && (
            <Flex
              gap="0.8rem"
              px={'1rem'}
              //  overflowX="auto"
              mb="2.7rem"
              overflow="auto"
              sx={{
                '::-webkit-scrollbar': {
                  // Adjust scrollbar height if needed
                  backgroundColor: 'black', // Set scrollbar background color to black
                },
                '::-webkit-scrollbar-thumb': {
                  backgroundColor: 'black', // Make scrollbar thumb blend with the background
                },
                '::-webkit-scrollbar-track': {
                  backgroundColor: 'black', // Set the track to black
                },
              }}
              whiteSpace="nowrap"
            >
              {Object.keys(
                upcomingWorkshopPerformanceSection?.programTypeList?.json
              )?.map(el => {
                const isActive =
                  filterId === sectionContent[1].programTypeList.json[el];
                return (
                  <Button
                    key={nanoid()}
                    rounded="none"
                    bg={isActive ? '#EDA807' : 'rgba(254, 250, 246, 0.05)'}
                    border="none"
                    fontFamily={
                      isActive
                        ? 'var(--FedraSansStd-medium)'
                        : 'var(--FedraSansStd-book)'
                    }
                    color={isActive ? '#030A14' : 'rgba(254, 250, 246, 0.5)'}
                    fontSize={{ base: '14px', md: '14px' }}
                    // p="8px 24px"
                    fontWeight="400"
                    _hover={{
                      background: '#EDA807',
                      color: '#030A14',
                      fontFamily: 'var(--FedraSansStd-medium)',
                      boxShadow: 'none',
                    }}
                    _active={{
                      background: '#EDA807',
                      color: '#030A14',
                      fontFamily: 'var(--FedraSansStd-medium)',
                      boxShadow: 'none',
                    }}
                    _focus={{ boxShadow: 'none' }}
                    onClick={() =>
                      setFilterId(sectionContent[1].programTypeList.json[el])
                    }
                    minW="auto"
                  >
                    {el}
                  </Button>
                );
              })}
            </Flex>
          )}
        </Box>
        <Box
          mx={'auto'}
          w="100%"
          pl={{ base: '0rem', md: '4rem' }}
          pr={{ base: '0', md: '4rem' }}
        >
          {extractedObjects?.filter(el => {
            if (filterId !== '') {
              return filterId.indexOf(el.category_id) > -1;
            } else {
              return el;
            }
          })?.length > 0 ? (
            <Slider {...settings} gap="20px">
              {extractedObjects
                .filter(el => {
                  if (filterId !== '') {
                    return filterId.indexOf(el.category_id) > -1;
                  } else {
                    return el;
                  }
                })
                .map((userCard, index) => {
                  return (
                    <WorkshopsCard
                      maxW={{
                        base: '280px',
                        lg: '270px',
                        xl: '270px',
                        '2xl': '322px',
                      }}
                      width={{
                        base: '280px',
                        lg: '270px',
                        xl: '270px',
                        '2xl': '322px',
                      }}
                      // width={{ base: '100% !important', md: '95% !important' }}
                      ml="12px"
                      mr="12px"
                      sectionTheme={upcomingWorkshopPerformanceSection?.theme}
                      key={index}
                      workShopDetails={userCard}
                    />
                  );
                })}
            </Slider>
          ) : (
            <>
              {!loading && (
                <Box color="#fff" textAlign="center" pt="80px">
                  <StructuredTextParser
                    color="#fff"
                    textAlign="center"
                    fontFamily="var(--FedraSansStd-book)"
                    str={render(
                      upcomingWorkshopPerformanceSection?.scheduleEmptyMessage
                    )}
                  />
                </Box>
              )}
            </>
          )}
          {loading && <Spinner />}
        </Box>
        <style global jsx>
          {`
            .upcomingSlider .slick-arrow::before {
              display: none;
            }
            .upcomingSlider .slick-arrow.slick-disabled {
              display: none;
            }
          `}
        </style>
      </Box>
    </Box>
  );
};

export default UpcomingList;
